import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Radio, Layers, Car as CarIcon, Users, ArrowRight, Flame } from "lucide-react";
import { vehicleTypeLabel } from "@/lib/labels";
import VehicleTypeIcon from "@/components/VehicleTypeIcon";

const TYPE_TINT = {
  car: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  moto: "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/10",
  trucks: "text-orange-300 border-orange-500/40 bg-orange-500/10",
  agri: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  atv: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  jetski: "text-violet-300 border-violet-500/40 bg-violet-500/10",
};

function typePill(t) {
  return TYPE_TINT[t] || "text-neutral-300 border-neutral-500/40 bg-neutral-500/10";
}

export default function Heatmap() {
  const [data, setData] = useState(null);
  const [sortBy, setSortBy] = useState("manufacturer_count");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/heatmap/protocols", {
          params: { top: 30, sort_by: sortBy },
        });
        if (!cancelled) setData(d);
      } catch (e) {
        toast.error("Heatmap konnte nicht geladen werden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortBy]);

  const maxVal = useMemo(() => {
    if (!data?.items?.length) return 1;
    return Math.max(
      ...data.items.map((r) =>
        sortBy === "vehicle_count"
          ? r.vehicle_count
          : sortBy === "ecu_count"
          ? r.ecu_count
          : r.manufacturer_count
      )
    );
  }, [data, sortBy]);

  return (
    <div data-testid="heatmap-page" className="space-y-10">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D32222] mb-3 flex items-center gap-2">
            <Flame className="w-3 h-3" /> Chip-Reuse Heatmap
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Welche Protokolle
            <br />
            <span className="text-[#D32222]">öffnen die meisten Fahrzeuge?</span>
          </h1>
          <p className="mt-4 text-neutral-400 max-w-2xl text-sm sm:text-base">
            Jeder Protokoll- oder MCU-Code definiert die Read/Flash-Prozedur
            unabhängig vom Hersteller. Diese Übersicht rankt sie nach
            Marken-Reichweite — perfekt, um zu sehen, welche Read-Methoden
            den größten Fahrzeug-Coverage bringen.
          </p>
        </div>
        {data && (
          <div className="grid grid-cols-3 gap-3">
            <BigTile
              value={data.total_protocols}
              label="Protokolle"
              testid="heatmap-total-protocols"
            />
            <BigTile
              value={data.grand_total_manufacturers}
              label="Hersteller"
              testid="heatmap-total-mfr"
            />
            <BigTile
              value={data.grand_total_vehicles}
              label="Fahrzeuge"
              testid="heatmap-total-vehicles"
            />
          </div>
        )}
      </div>

      {/* Sort controls */}
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-widest text-neutral-500">
          Sortieren nach
        </span>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger
            data-testid="heatmap-sort"
            className="w-[240px] bg-neutral-900 border-neutral-800 text-neutral-100 focus:ring-[#D32222]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-panel border-neutral-800 text-neutral-100">
            <SelectItem
              value="manufacturer_count"
              data-testid="heatmap-sort-mfr"
              className="focus:bg-neutral-800"
            >
              Marken-Reichweite
            </SelectItem>
            <SelectItem
              value="vehicle_count"
              data-testid="heatmap-sort-veh"
              className="focus:bg-neutral-800"
            >
              Fahrzeug-Anzahl
            </SelectItem>
            <SelectItem
              value="ecu_count"
              data-testid="heatmap-sort-ecu"
              className="focus:bg-neutral-800"
            >
              ECU-Varianten
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Ranking */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-24 bg-panel rounded-xl animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div data-testid="heatmap-list" className="space-y-2">
          {data.items.map((r, i) => (
            <HeatmapRow
              key={r.protocol}
              rank={i + 1}
              row={r}
              sortBy={sortBy}
              maxVal={maxVal}
            />
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="text-xs text-neutral-500 border-t border-neutral-900 pt-4">
        Die Balkenlänge zeigt den relativen Wert nach der aktuellen
        Sortierung. Klick auf eine Zeile öffnet die Kompatibilitätsliste
        gefiltert nach Protokoll-Code.
      </div>
    </div>
  );
}

function BigTile({ value, label, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-neutral-800 bg-panel px-4 py-3 min-w-[110px]"
    >
      <div
        className="text-2xl text-[#D32222] tabular-nums"
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {value?.toLocaleString?.("de-DE") ?? value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function HeatmapRow({ rank, row, sortBy, maxVal }) {
  const primary =
    sortBy === "vehicle_count"
      ? row.vehicle_count
      : sortBy === "ecu_count"
      ? row.ecu_count
      : row.manufacturer_count;
  const pct = Math.max(4, Math.round((primary / maxVal) * 100));

  return (
    <Link
      to={`/?search=${encodeURIComponent(row.protocol)}`}
      data-testid={`heatmap-row-${row.protocol}`}
      className="group relative block rounded-xl border border-neutral-800 bg-panel p-4 lg:p-5 hover:border-[#D32222]/50 transition overflow-hidden"
    >
      {/* Bar */}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D32222]/20 via-[#D32222]/10 to-transparent transition-all"
        style={{ width: `${pct}%` }}
      />

      <div className="relative grid grid-cols-1 md:grid-cols-[80px_1.4fr_1fr_1fr_auto] items-center gap-4">
        {/* Rank + protocol */}
        <div>
          <div
            className="text-xs text-neutral-500 font-mono tabular-nums"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            #{String(rank).padStart(2, "0")}
          </div>
        </div>
        <div>
          <div
            className="text-2xl sm:text-3xl text-neutral-100 group-hover:text-[#EB4E4E] transition tabular-nums flex items-center gap-2"
            style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "-0.02em" }}
          >
            <Radio className="w-4 h-4 text-[#D32222]" /> {row.protocol}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {row.vehicle_types.map((t) => (
              <span
                key={t}
                className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest border px-1.5 py-0.5 rounded ${typePill(t)}`}
              >
                <VehicleTypeIcon type={t} className="h-4 w-5" />
                {vehicleTypeLabel(t)}
              </span>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 md:grid-cols-3 gap-2 md:col-span-2">
          <MiniStat
            icon={<Users className="w-3 h-3" />}
            value={row.manufacturer_count}
            label="Hersteller"
            highlight={sortBy === "manufacturer_count"}
          />
          <MiniStat
            icon={<Layers className="w-3 h-3" />}
            value={row.ecu_count}
            label="ECUs"
            highlight={sortBy === "ecu_count"}
          />
          <MiniStat
            icon={<CarIcon className="w-3 h-3" />}
            value={row.vehicle_count}
            label="Fahrzeuge"
            highlight={sortBy === "vehicle_count"}
          />
        </div>

        {/* Arrow */}
        <ArrowRight className="w-4 h-4 text-neutral-600 group-hover:text-[#EB4E4E] transition opacity-0 group-hover:opacity-100 hidden md:block" />
      </div>

      {/* Manufacturers preview */}
      <div className="relative mt-3 pt-3 border-t border-neutral-900 flex flex-wrap gap-1.5">
        {row.manufacturers.slice(0, 10).map((m) => (
          <span
            key={m}
            className="text-[10px] uppercase tracking-widest text-neutral-400 border border-neutral-800 rounded px-1.5 py-0.5"
          >
            {m}
          </span>
        ))}
        {row.manufacturer_count > 10 && (
          <span className="text-[10px] uppercase tracking-widest text-neutral-500 border border-transparent rounded px-1.5 py-0.5">
            +{row.manufacturer_count - 10} mehr
          </span>
        )}
      </div>
    </Link>
  );
}

function MiniStat({ icon, value, label, highlight }) {
  return (
    <div
      className={`rounded px-2 py-1.5 border ${
        highlight
          ? "border-[#D32222]/50 bg-[#D32222]/10"
          : "border-neutral-800 bg-overlay/30"
      }`}
    >
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-neutral-500">
        {icon} {label}
      </div>
      <div
        className={`text-xl tabular-nums ${highlight ? "text-[#EB4E4E]" : "text-neutral-100"}`}
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {value?.toLocaleString?.("de-DE") ?? value}
      </div>
    </div>
  );
}
